import { Download, Eye } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { downloadCabinetDocument, DOCUMENTS_BASE, getCabinetDocumentBlob } from '../api';
import {
  CabinetEmptyState,
  CabinetListFooter,
  CabinetListToolbar,
} from '../components/listChrome';
import { createCabinetViewStore, viewModes } from '../store/cabinetView';
import { useCabinetDocuments, useSignCabinetDocument } from '../queries';
import type { CabinetDocument, CabinetDocumentStatus, CabinetDocumentType } from '../types';

const useDocumentsUi = createCabinetViewStore('cabinet.documents:ui', 'table');
const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
type DocumentColumn = 'name' | 'type' | 'status';

const TYPE_LABEL: Record<CabinetDocumentType, string> = {
  agreement: 'Agreement',
  sow: 'Statement of Work',
  nda: 'NDA',
  offer: 'Offer letter',
  proposal: 'Proposal',
  cv: 'CV',
  email: 'Email',
  project_materials: 'Project Materials',
  other: 'Other',
};

const STATUS_LABEL: Record<CabinetDocumentStatus, string> = {
  draft: 'Draft',
  rendered: 'Submitted',
  pending_signature: 'Pending signature',
  signed: 'Signed',
  void: 'Void',
};

const STATUS_ORDER: CabinetDocumentStatus[] = [
  'pending_signature',
  'rendered',
  'signed',
  'draft',
  'void',
];

function statusVariant(s: CabinetDocumentStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'signed') return 'default';
  if (s === 'pending_signature') return 'secondary';
  if (s === 'void') return 'destructive';
  return 'outline';
}

const signable = (s: CabinetDocumentStatus) => s === 'rendered' || s === 'pending_signature';
const fileName = (d: CabinetDocument) => (d.file ? d.file.split('/').pop() || `${d.name}.pdf` : `${d.name}.pdf`);

export function CabinetDocumentsPage() {
  const view = useDocumentsUi((s) => s.view);
  const setView = useDocumentsUi((s) => s.setView);
  const ordering = useDocumentsUi((s) => s.ordering);
  const setOrdering = useDocumentsUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useCabinetDocuments({
    page,
    search: search || undefined,
    ordering,
  });
  const sign = useSignCabinetDocument();
  const rows = data?.results ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Documents</h1>

      <CabinetListToolbar
        view={view}
        onViewChange={setView}
        modes={MODES}
        search={{
          value: searchInput,
          onChange: (v) => {
            setPage(1);
            setSearchInput(v);
          },
          placeholder: 'Search documents…',
        }}
        exportBase={DOCUMENTS_BASE}
        exportParams={{ search: search || undefined, ordering }}
      />

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <DocumentsTable
          rows={rows}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onSign={(id) => sign.mutate(id)}
          signPending={sign.isPending}
        />
      ) : view === 'list' ? (
        <DocumentsList rows={rows} />
      ) : view === 'cards' ? (
        <DocumentsCards rows={rows} />
      ) : (
        <DocumentsGroupedByStatus rows={rows} />
      )}

      <CabinetListFooter
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        hasPrev={Boolean(data?.previous)}
        hasNext={Boolean(data?.next)}
        isFetching={isFetching}
        noun={['document', 'documents']}
      />
    </div>
  );
}

interface DocumentsTableProps {
  rows: CabinetDocument[];
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onSign: (id: number) => void;
  signPending: boolean;
}

function DocumentsTable({ rows, ordering, onOrderingChange, onSign, signPending }: DocumentsTableProps) {
  const [preview, setPreview] = useState<CabinetDocument | null>(null);
  const previewId = preview?.id ?? null;
  const fetchPreviewBlob = useCallback(
    () => getCabinetDocumentBlob(previewId as number),
    [previewId],
  );
  return (
    <>
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<DocumentColumn> column="name" value={ordering} onChange={onOrderingChange}>
              Name
            </SortableTableHead>
            <SortableTableHead<DocumentColumn> column="type" value={ordering} onChange={onOrderingChange}>
              Type
            </SortableTableHead>
            <SortableTableHead<DocumentColumn> column="status" value={ordering} onChange={onOrderingChange}>
              Status
            </SortableTableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-10 text-center">
                No documents yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell className="text-muted-foreground">{TYPE_LABEL[d.type]}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(d.status)}>{STATUS_LABEL[d.status]}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {d.file ? (
                      <>
                        <Button
                          variant="outline"
                          size="icon"
                          className="size-8"
                          aria-label={`Preview ${d.name}`}
                          title="Preview"
                          onClick={() => setPreview(d)}
                        >
                          <Eye className="size-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="size-8"
                          aria-label={`Download ${d.name}`}
                          title="Download"
                          onClick={() => downloadCabinetDocument(d.id, fileName(d))}
                        >
                          <Download className="size-4" />
                        </Button>
                      </>
                    ) : null}
                    {signable(d.status) ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm">Sign</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Sign “{d.name}”?</AlertDialogTitle>
                            <AlertDialogDescription>
                              By signing you acknowledge and accept this document.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onSign(d.id)} disabled={signPending}>
                              Sign
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : null}
                    {!d.file && !signable(d.status) ? (
                      <span className="text-muted-foreground text-sm">—</span>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
    <PdfPreviewDialog
      open={Boolean(preview)}
      onOpenChange={(o) => !o && setPreview(null)}
      title={preview?.name ?? ''}
      fetchBlob={previewId ? fetchPreviewBlob : undefined}
    />
    </>
  );
}

function DocumentsList({ rows }: { rows: CabinetDocument[] }) {
  if (rows.length === 0) return <CabinetEmptyState>No documents yet.</CabinetEmptyState>;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((d) => (
        <li key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 truncate font-semibold">{d.name}</span>
          <span className="text-muted-foreground hidden sm:inline">{TYPE_LABEL[d.type]}</span>
          <Badge variant={statusVariant(d.status)} className="shrink-0">
            {STATUS_LABEL[d.status]}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

function DocumentsCards({ rows }: { rows: CabinetDocument[] }) {
  if (rows.length === 0) return <CabinetEmptyState>No documents yet.</CabinetEmptyState>;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((d) => (
        <Card key={d.id} className="min-w-0">
          <CardHeader>
            <CardTitle className="flex min-w-0 items-center justify-between gap-2 text-base">
              <span className="min-w-0 flex-1 truncate">{d.name}</span>
              <Badge variant={statusVariant(d.status)} className="shrink-0">
                {STATUS_LABEL[d.status]}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground text-xs">{TYPE_LABEL[d.type]}</CardContent>
        </Card>
      ))}
    </div>
  );
}

function DocumentsGroupedByStatus({ rows }: { rows: CabinetDocument[] }) {
  const groups = useMemo(
    () =>
      STATUS_ORDER.map((s) => ({
        key: s,
        label: STATUS_LABEL[s],
        bucket: rows.filter((r) => r.status === s),
      })).filter((g) => g.bucket.length > 0),
    [rows],
  );
  if (rows.length === 0) return <CabinetEmptyState>No documents yet.</CabinetEmptyState>;
  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key}>
          <h2 className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <DocumentsCards rows={bucket} />
        </section>
      ))}
    </div>
  );
}
